/**
 * BacktestPanel — Backtest configuration tab for the Strategy Builder.
 *
 * Provides period/custom date selection, capital, slippage, fees,
 * and a Run Backtest button. Displayed in the right panel of the builder.
 */

import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { useStrategy } from '../../stores/builderStore';
import { generateDSL } from '../../services/dslService';
import { runBacktest } from '../../services/api';
import type { BacktestResult } from '../../stores/backtestStore';

const TIME_PERIODS = [
  { label: '1M', days: 21 },
  { label: '3M', days: 63 },
  { label: '6M', days: 126 },
  { label: '1Y', days: 252 },
  { label: '3Y', days: 756 },
  { label: '5Y', days: 1260 },
  { label: '7Y', days: 1764 },
  { label: '10Y', days: 2520 },
];

interface BacktestPanelProps {
  onResult: (result: BacktestResult) => void;
  onError: (error: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export function BacktestPanel({ onResult, onError, isLoading, setIsLoading }: BacktestPanelProps) {
  const strategy = useStrategy();

  const [selectedPeriod, setSelectedPeriod] = useState('1Y');
  const [days, setDays] = useState(252);
  const [dateMode, setDateMode] = useState<'period' | 'custom'>('period');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [initialCapital, setInitialCapital] = useState(100000);
  const [slippageBps, setSlippageBps] = useState(1);
  const [includeRegulatoryFees, setIncludeRegulatoryFees] = useState(true);
  const [useCache, setUseCache] = useState(true);

  const handlePeriodClick = (period: { label: string; days: number }) => {
    setSelectedPeriod(period.label);
    setDays(period.days);
  };

  const handleRun = useCallback(async () => {
    const code = generateDSL(strategy);
    if (!code.trim()) {
      onError('Strategy is empty — add some nodes first');
      return;
    }

    if (dateMode === 'custom') {
      if (!startDate || !endDate) {
        onError('Please select both start and end dates');
        return;
      }
      if (new Date(startDate) >= new Date(endDate)) {
        onError('Start date must be before end date');
        return;
      }
    }

    setIsLoading(true);
    onError('');

    try {
      const result = await runBacktest(code, days, {
        useCache,
        useFastMode: true,
        startDate: dateMode === 'custom' ? startDate : undefined,
        endDate: dateMode === 'custom' ? endDate : undefined,
        initialCapital,
        slippageBps,
        includeRegulatoryFees,
      });
      onResult(result);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Backtest failed');
    } finally {
      setIsLoading(false);
    }
  }, [strategy, days, dateMode, startDate, endDate, initialCapital, slippageBps, includeRegulatoryFees, useCache, onResult, onError, setIsLoading]);

  return (
    <div className="flex flex-col h-full overflow-y-auto custom-scrollbar">
      <div className="p-3 space-y-4">
        {/* Period / Custom toggle */}
        <div>
          <label className="text-[10px] font-medium text-surface-500 uppercase tracking-wide mb-1.5 block">
            Time Range
          </label>
          <div className="flex gap-1 mb-2">
            <button
              onClick={() => setDateMode('period')}
              className={clsx(
                'flex-1 text-[11px] font-medium py-1.5 rounded-md transition-colors',
                dateMode === 'period'
                  ? 'bg-primary-500 text-white'
                  : 'bg-surface-700 text-surface-400 hover:text-surface-200',
              )}
            >
              Period
            </button>
            <button
              onClick={() => setDateMode('custom')}
              className={clsx(
                'flex-1 text-[11px] font-medium py-1.5 rounded-md transition-colors',
                dateMode === 'custom'
                  ? 'bg-primary-500 text-white'
                  : 'bg-surface-700 text-surface-400 hover:text-surface-200',
              )}
            >
              Custom
            </button>
          </div>

          {dateMode === 'period' ? (
            <div className="grid grid-cols-4 gap-1">
              {TIME_PERIODS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => handlePeriodClick(p)}
                  className={clsx(
                    'text-[11px] font-medium py-1.5 rounded-md transition-colors',
                    selectedPeriod === p.label
                      ? 'bg-primary-500/20 text-primary-400 ring-1 ring-primary-500/40'
                      : 'bg-surface-700/50 text-surface-400 hover:text-surface-200 hover:bg-surface-700',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <div>
                <label className="text-[10px] text-surface-500 mb-1 block">Start</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs rounded-md bg-surface-900 border border-surface-600 text-surface-200 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
                />
              </div>
              <div>
                <label className="text-[10px] text-surface-500 mb-1 block">End</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-2 py-1.5 text-xs rounded-md bg-surface-900 border border-surface-600 text-surface-200 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
                />
              </div>
            </div>
          )}
        </div>

        {/* Initial Capital */}
        <div>
          <label className="text-[10px] font-medium text-surface-500 uppercase tracking-wide mb-1.5 block">
            Initial Capital
          </label>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-500 text-xs">$</span>
            <input
              type="number"
              value={initialCapital}
              onChange={(e) => setInitialCapital(Number(e.target.value) || 100000)}
              min={1000}
              step={1000}
              className="w-full pl-6 pr-2 py-1.5 text-xs rounded-md bg-surface-900 border border-surface-600 text-surface-200 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
            />
          </div>
        </div>

        {/* Slippage */}
        <div>
          <label className="text-[10px] font-medium text-surface-500 uppercase tracking-wide mb-1.5 block">
            Slippage
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={slippageBps}
              onChange={(e) => setSlippageBps(Number(e.target.value) || 0)}
              min={0}
              max={100}
              step={1}
              className="flex-1 px-2 py-1.5 text-xs rounded-md bg-surface-900 border border-surface-600 text-surface-200 focus:outline-none focus:ring-1 focus:ring-primary-500/40"
            />
            <span className="text-[10px] text-surface-500">bps</span>
          </div>
        </div>

        {/* Checkboxes */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={includeRegulatoryFees}
              onChange={(e) => setIncludeRegulatoryFees(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500/30"
            />
            <span className="text-[11px] text-surface-400 group-hover:text-surface-300 transition-colors">
              Regulatory Fees
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={useCache}
              onChange={(e) => setUseCache(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500/30"
            />
            <span className="text-[11px] text-surface-400 group-hover:text-surface-300 transition-colors">
              Use Cache
            </span>
          </label>
        </div>

        {/* Run Button */}
        <button
          onClick={handleRun}
          disabled={isLoading}
          className={clsx(
            'w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all',
            isLoading
              ? 'bg-primary-500/30 text-primary-300 cursor-wait'
              : 'bg-primary-500 text-white hover:bg-primary-600 shadow-lg shadow-primary-500/20',
          )}
        >
          {isLoading ? (
            <>
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Running...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Run Backtest
            </>
          )}
        </button>
      </div>
    </div>
  );
}
